import React, { useState } from 'react';
import { DualDateRangePicker, Modal, Button } from '@crisellevaldez/speculo-ui';
import { SlidersHorizontal } from 'lucide-react';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { convertDateRangeForDisplay } from '../../../../utils/dateRangeTimezone';

interface ActivityTableHeaderProps {
  title: string;
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  children?: React.ReactNode; // For additional filters
  minDate?: Date; // Optional minimum date
  count?: number; // Optional count of records
  isLoading?: boolean; // Optional loading state for count
  showDateRange?: boolean; // Optional flag to show/hide date range picker
}

/**
 * Tab-content section header — uppercase eyebrow title + count + filter
 * cluster. Sits flat on the parent surface; no icon or background chrome.
 *
 * On mobile (< lg) the filter cluster collapses behind a "Filters" button
 * that opens a modal — inline filters wrap awkwardly on narrow screens.
 */
export const ActivityTableHeader: React.FC<ActivityTableHeaderProps> = ({
  title,
  dateRange,
  onDateRangeChange,
  children,
  minDate,
  count,
  isLoading = false,
  showDateRange = true,
}) => {
  const { organization } = useOrganizationStore();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const showInlineDate = showDateRange !== false && dateRange && onDateRangeChange;

  return (
    <div className="flex flex-row items-center justify-between gap-2">
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground whitespace-nowrap">
          {title}
        </span>
        {count !== undefined && !isLoading && (
          <span className="text-[10.5px] tabular-nums text-muted-foreground">
            ({count.toLocaleString()})
          </span>
        )}
      </div>

      {/* Inline filter cluster — hidden on mobile, shown lg+ */}
      <div className="hidden lg:flex items-center gap-2 flex-nowrap">
        <div className="activity-filter-compact contents">{children}</div>

        {showInlineDate && (
          <div className="w-fit">
            <DualDateRangePicker
              value={convertDateRangeForDisplay(dateRange, organization?.timezone)}
              onChange={onDateRangeChange}
              showPresets={true}
              minDate={minDate}
            />
          </div>
        )}
      </div>

      {/* Mobile "Filters" trigger — opens a modal with the same controls */}
      {(children || showInlineDate) && (
        <Button
          variant="outline"
          size="sm"
          className="lg:hidden flex-shrink-0"
          onClick={() => setMobileFiltersOpen(true)}
        >
          <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
          Filters
        </Button>
      )}

      <Modal
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        size="sm"
      >
        <Modal.Header>Filters</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-3">
            {children && <div className="flex flex-col gap-2">{children}</div>}
            {showInlineDate && (
              <div className="w-full">
                <DualDateRangePicker
                  value={convertDateRangeForDisplay(dateRange, organization?.timezone)}
                  onChange={onDateRangeChange}
                  showPresets={true}
                  minDate={minDate}
                />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setMobileFiltersOpen(false)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
